import { Cell, Grid, useTheme } from 'bold-ui'
import { dateFnsDefaultLocale } from 'components/agenda/agendaLocalizer'
import { SelectField } from 'components/form'
import { CustomSingleSelectFieldProps, ErrorField } from 'components/form/final-form'
import { addMinutes, format } from 'date-fns'
import { useHorarioAgendaSelectFieldQuery } from 'graphql/hooks.generated'
import { ReactComponent as AgendaOnlineIcon } from 'images/agenda/agenda-online.svg'
import { useCallback, useEffect, useMemo } from 'react'
import { useField } from 'react-final-form'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'

import { getHorarioInicialStatus as getHorarioInicialStatusPure } from './getHorarioInicialStatus'
import { getItemsHorarioFinal } from './getItemsHorarioFinal'
import { getProximoHorarioIndisponivel } from './getProximoHorarioIndisponivel'
import { hasHorarioOcupadoNoPeriodo } from './hasHorarioOcupadoNoPeriodo'

export interface HorarioAgendaSelectFieldModel {
  inicial: Date
  final: Date
}

export type HorarioAgendaSelectFieldProps = DistributiveOmit<
  CustomSingleSelectFieldProps<Date>,
  'name' | 'label' | 'value'
> & {
  name: MetaPath<HorarioAgendaSelectFieldModel>
  lotacaoId: ID
  outraLotacaoIdVerificarDisponibilidade?: ID
  showHorarioFinal?: boolean
  dia: Date | undefined
  value?: HorarioAgendaSelectFieldModel
  labels?: { inicial: string; final?: string }
  verificarReservasAgendaOnline?: boolean
  atencaoDomiciliar?: boolean
  agendamentosIdsDesconsiderar?: Array<ID>
}

const itemToString = (item: Date) => item && format(item, 'HH:mm', { locale: dateFnsDefaultLocale })

export function HorarioAgendaSelectField(props: Readonly<HorarioAgendaSelectFieldProps>) {
  const {
    name,
    lotacaoId,
    outraLotacaoIdVerificarDisponibilidade,
    value,
    dia,
    showHorarioFinal = false,
    labels = { inicial: 'Horário inicial', final: 'Horário final' },
    disabled,
    verificarReservasAgendaOnline = true,
    atencaoDomiciliar = false,
    agendamentosIdsDesconsiderar,
    ...rest
  } = props

  const theme = useTheme()

  const isDiaVazio = !dia

  const { data: { horariosAgenda } = {}, loading } = useHorarioAgendaSelectFieldQuery({
    variables: {
      input: {
        lotacaoId,
        outraLotacaoIdVerificarDisponibilidade,
        dia: dateAsYyyyMmDd(dia),
        isAtencaoDomiciliar: atencaoDomiciliar,
        agendamentosIdsDesconsiderar: agendamentosIdsDesconsiderar?.filterNotNull(),
      },
    },
    skip: isDiaVazio || disabled || !lotacaoId,
  })

  const {
    input: { value: horarioInicial, onChange: onChangeHorarioInicial, onBlur: onBlurHorarioInicial },
  } = useField<Date>(name.inicial.absolutePath())
  const {
    input: { onChange: onChangeHorarioFinal },
  } = useField<Date>(name.final.absolutePath())

  // TODO https://github.com/laboratoriobridge/pec/issues/29585
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHorarioInicial = useCallback(onChangeHorarioInicial, [name.inicial])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHorarioFinal = useCallback(onChangeHorarioFinal, [name.final])

  const horariosMap = useMemo(() => new Map(horariosAgenda?.map((h) => [new Date(h.horario), h])), [horariosAgenda])

  const horariosIniciais = useMemo(() => Array.from(horariosMap.keys()).sort((a, b) => Number(a) - Number(b)), [
    horariosMap,
  ])

  useEffect(() => {
    if (!loading) {
      const horarioDia = horarioInicial
        ? horariosIniciais.find(
            (h) => h.getHours() === horarioInicial.getHours() && h.getMinutes() === horarioInicial.getMinutes()
          )
        : undefined

      setHorarioInicial(horarioDia)
      onBlurHorarioInicial() //Necessário para revalidar o campo
    }
  }, [horariosIniciais, horarioInicial, setHorarioInicial, loading, onBlurHorarioInicial])

  useEffect(() => {
    if (isDiaVazio) {
      setHorarioFinal(undefined)
    }
  }, [isDiaVazio, setHorarioFinal])

  // TODO (RNG: #31450): Quando todos os módulos possuírem horário final, remover esse useEffect
  useEffect(() => {
    if (!horarioInicial) {
      setHorarioFinal(undefined)
    } else if (!loading && !showHorarioFinal) {
      const horario = horariosMap.get(horarioInicial)
      if (horario) {
        setHorarioFinal(addMinutes(horarioInicial, horario.duracao))
      }
    }
  }, [horarioInicial, horariosMap, loading, setHorarioFinal, showHorarioFinal])

  const getHorarioInicialStatus = (h: Date | undefined) =>
    getHorarioInicialStatusPure(h, horariosMap, verificarReservasAgendaOnline)

  const horariosAgendaNormalizados = useMemo(
    () =>
      horariosAgenda?.map((h) => ({
        ...h,
        isOcupado: h.isOcupado || (verificarReservasAgendaOnline && h.isReservadoAgendaOnline),
      })),
    [horariosAgenda, verificarReservasAgendaOnline]
  )

  const proximoHorarioOcupado = useMemo(
    () => getProximoHorarioIndisponivel(horarioInicial, horariosAgendaNormalizados),
    [horarioInicial, horariosAgendaNormalizados]
  )

  const horariosFinais = useMemo(
    () => (showHorarioFinal ? getItemsHorarioFinal(horariosMap, horarioInicial, proximoHorarioOcupado) : []),
    [horarioInicial, horariosMap, proximoHorarioOcupado, showHorarioFinal]
  )

  return (
    <Grid>
      <Cell size={showHorarioFinal ? 6 : 12}>
        <SelectField<Date>
          label={labels.inicial}
          value={value?.inicial}
          items={horariosIniciais}
          itemToString={itemToString}
          itemIsEqual={(h) => getHorarioInicialStatus(h) !== 'disponivel'}
          customText={(h) => (getHorarioInicialStatus(h) === 'ocupado' ? 'Ocupado' : 'Reservado')}
          selectedItemStyle={(h) =>
            getHorarioInicialStatus(h) === 'ocupado'
              ? { icon: 'banOutline', color: theme.pallete.text.disabled }
              : { icon: AgendaOnlineIcon, color: theme.pallete.status.info.main }
          }
          selectedItemTooltip={(h) =>
            getHorarioInicialStatus(h) === 'reservadoAgendaOnline' ? 'Reservado para agenda online' : ''
          }
          name={name.inicial}
          disabled={disabled}
          validate={(h) => (getHorarioInicialStatus(h) !== 'disponivel' ? 'Horário ocupado' : undefined)}
          {...rest}
        />
        <ErrorField name={name} ignoreObjectError />
      </Cell>
      {showHorarioFinal && (
        <Cell size={6}>
          <SelectField<Date>
            label={labels.final}
            value={value?.final}
            items={horariosFinais}
            itemToString={itemToString}
            name={name.final}
            disabled={disabled || !horarioInicial}
            validate={(horarioFinal) =>
              hasHorarioOcupadoNoPeriodo(horarioInicial, horarioFinal, horariosAgenda, verificarReservasAgendaOnline)
                ? 'Horário possui agendamento / reserva'
                : undefined
            }
            {...rest}
          />
        </Cell>
      )}
    </Grid>
  )
}
